import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { groupChatText, mergeGroupChatMessages } from '../chat/group-chat';
import { evictGroupChatOnRefusal } from '../chat/group-chat-cache';
import { watchGroupChat } from '../chat/group-chat-subscription';
import { mostTellingError } from '../domain/access-refusal';
import { useGroupChatTransport, useSession } from './provider';
import { useMe } from './use-me';

function useChatContext(groupId: string | undefined) {
  const transport = useGroupChatTransport();
  const session = useSession();
  const me = useMe(!!transport && !!groupId);
  const viewerId = me.data?.uuid;
  const enabled = !!transport && !!groupId && !!viewerId && session.status === 'authenticated';
  const queryClient = useQueryClient();
  const [deliveryError, setDeliveryError] = useState<Error | null>(null);
  const subscription = useRef<ReturnType<typeof watchGroupChat> | undefined>(undefined);
  useEffect(() => {
    setDeliveryError(null);
    if (!enabled || !transport || !groupId || !viewerId) return;
    const current = watchGroupChat(transport, viewerId, groupId, {
      change() {
        setDeliveryError(null);
        void queryClient.invalidateQueries({ queryKey: ['group-chat', viewerId, groupId] });
      },
      error(error) {
        evictGroupChatOnRefusal(queryClient, viewerId, groupId, error);
        setDeliveryError(error);
      },
    });
    subscription.current = current;
    return () => {
      current.dispose();
      subscription.current = undefined;
    };
  }, [enabled, transport, groupId, viewerId, queryClient]);
  const retryDelivery = useCallback(() => {
    setDeliveryError(null);
    subscription.current?.retry();
  }, []);
  return {
    transport,
    enabled,
    viewerId,
    queryClient,
    deliveryError,
    retryDelivery,
    identityError: me.error,
    retryIdentity: me.refetch,
  };
}

/** Text-only chat. Durability, unread counts and access decisions belong to the transport. */
export function useGroupChat(groupId: string | undefined) {
  const {
    transport,
    enabled,
    viewerId,
    queryClient,
    deliveryError,
    retryDelivery,
    identityError,
    retryIdentity,
  } = useChatContext(groupId);
  const queryKey = ['group-chat', viewerId, groupId, 'history'] as const;
  const query = useInfiniteQuery({
    queryKey,
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      if (!transport || !groupId) throw new Error('Group chat backend is not configured');
      return transport.list(groupId, pageParam);
    },
    getNextPageParam: (page) => page.cursor ?? undefined,
    retry: false,
  });
  const send = useMutation({
    mutationFn: async (text: string) => {
      if (!enabled || !transport || !groupId)
        throw new Error('Group chat backend is not configured');
      await transport.send(groupId, groupChatText(text));
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['group-chat', viewerId, groupId] }),
    retry: false,
  });
  const markRead = useCallback(async () => {
    if (!enabled || !transport || !groupId) return;
    await transport.markRead(groupId);
    await queryClient.invalidateQueries({ queryKey: ['group-chat', viewerId, groupId] });
  }, [enabled, transport, groupId, viewerId, queryClient]);
  const pages = enabled ? (query.data?.pages ?? []) : [];
  return {
    messages: mergeGroupChatMessages(pages),
    isLoading:
      !!transport && !!groupId && ((!viewerId && !identityError) || (enabled && query.isPending)),
    error: mostTellingError(query.error, deliveryError, identityError),
    isAvailable: !!transport,
    unavailableReason: transport ? null : ('backend-not-configured' as const),
    unreadCount: pages[0]?.unreadCount ?? 0,
    firstUnreadMessageId: pages[0]?.firstUnreadMessageId ?? null,
    hasMore: enabled && query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: async () => {
      if (enabled && query.hasNextPage && !query.isFetchingNextPage) await query.fetchNextPage();
    },
    sendMessage: send.mutateAsync,
    isSending: send.isPending,
    sendError: send.error,
    retry: () => {
      retryDelivery();
      if (!viewerId) void retryIdentity();
      else if (enabled) void query.refetch();
    },
    markRead,
  };
}

/** Fetch only the latest page; profile cards never page the full chat history. */
export function useGroupChatSummary(groupId: string | undefined) {
  const { transport, enabled, viewerId, deliveryError, identityError } = useChatContext(groupId);
  const query = useQuery({
    queryKey: ['group-chat', viewerId, groupId, 'summary'],
    enabled,
    queryFn: () => {
      if (!transport || !groupId) throw new Error('Group chat backend is not configured');
      return transport.list(groupId);
    },
    retry: false,
  });
  return {
    unreadCount: enabled ? (query.data?.unreadCount ?? 0) : 0,
    latestMessage:
      enabled && query.data ? (mergeGroupChatMessages([query.data]).at(-1) ?? null) : null,
    isLoading:
      !!transport && !!groupId && ((!viewerId && !identityError) || (enabled && query.isPending)),
    isAvailable: !!transport,
    error: mostTellingError(query.error, deliveryError, identityError),
  };
}
